import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

const nav = [
  { label: "Shop", to: "/shop" },
  { label: "Categories", to: "/categories" },
  { label: "Track Order", to: "/track" },
  { label: "Support", to: "/#faq" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const nav_ = useNavigate();
  const loc = useLocation();
  const [openMenu, setOpenMenu] = React.useState(false);
  const [openAcct, setOpenAcct] = React.useState(false);
  const [q, setQ] = React.useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    nav_(`/shop?q=${encodeURIComponent(q.trim())}`);
    setOpenMenu(false);
  };

  return (
    <header className="sticky top-0 z-50 kyro-glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5 shrink-0">
          <div className="w-9 h-9 rounded-full bg-[#FF4433] grid place-items-center coral-glow relative">
            <span className="font-display font-black text-white text-base leading-none">B</span>
            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#F4F2EC]" />
          </div>
          <div className="leading-none">
            <div className="font-display font-black text-[15px] tracking-[0.02em] text-kyro-ink">BABA MEDIA</div>
            <div className="text-[9px] tracking-[0.28em] text-kyro-faint uppercase mt-0.5">Premium Subscriptions</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-kyro-mute">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`hover:text-kyro-ink transition-colors ${loc.pathname === n.to ? "text-kyro-ink" : ""}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-4 pr-2 py-2 w-72">
          <Search className="w-4 h-4 text-kyro-mute" />
          <input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-transparent outline-none w-full text-sm placeholder:text-kyro-faint text-kyro-ink"
            placeholder="Search subscriptions..."
          />
        </form>

        <div className="flex items-center gap-2">
          <button
            data-testid="open-cart-btn"
            onClick={() => setCartOpen(true)}
            className="relative w-10 h-10 grid place-items-center rounded-full border border-white/10 hover:bg-white/5 transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF4433] text-white text-[10px] font-bold rounded-full grid place-items-center">{count}</span>
            )}
          </button>

          {user ? (
            <div className="relative">
              <button
                data-testid="account-menu-btn"
                onClick={() => setOpenAcct((o) => !o)}
                className="hidden md:flex items-center gap-2 h-10 px-3 rounded-full border border-white/10 hover:bg-white/5 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span className="text-sm max-w-24 truncate">{user.name || user.email}</span>
              </button>
              {openAcct && (
                <div className="absolute right-0 mt-2 w-56 kyro-glass rounded-xl p-2 shadow-2xl">
                  <div className="px-3 py-2 text-xs text-kyro-mute border-b border-white/5 mb-1">{user.email}</div>
                  <Link to="/account" onClick={() => setOpenAcct(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5" data-testid="acct-orders-link">
                    <ShoppingBag className="w-4 h-4" /> My Orders
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admincontrol" onClick={() => setOpenAcct(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5" data-testid="acct-admin-link">
                      <LayoutDashboard className="w-4 h-4" /> Admin Panel
                    </Link>
                  )}
                  <button onClick={async () => { setOpenAcct(false); await logout(); nav_("/"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5 text-left" data-testid="acct-logout-btn">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="login-cta-btn"
              className="hidden md:inline-flex items-center h-10 px-5 rounded-full bg-white text-black text-sm font-semibold hover:bg-kyro-ink transition-colors"
            >
              Sign in
            </Link>
          )}

          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-white/10"
            onClick={() => setOpenMenu((o) => !o)}
          >
            {openMenu ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {openMenu && (
        <div className="lg:hidden border-t border-white/5 bg-[#0b0d10]/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-4 pr-2 py-2">
              <Search className="w-4 h-4 text-kyro-mute" />
              <input value={q} onChange={(e) => setQ(e.target.value)} className="bg-transparent outline-none w-full text-sm placeholder:text-kyro-faint" placeholder="Search subscriptions..." data-testid="mobile-search-input" />
            </form>
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpenMenu(false)} className="text-sm text-kyro-ink" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</Link>
            ))}
            {!user && (
              <Link to="/login" onClick={() => setOpenMenu(false)} className="kyro-btn-primary text-center" data-testid="mobile-login-btn">Sign in</Link>
            )}
            {user && (
              <>
                <Link to="/account" onClick={() => setOpenMenu(false)} className="text-sm text-kyro-ink">My Orders</Link>
                {user.role === "admin" && (
                  <Link to="/admincontrol" onClick={() => setOpenMenu(false)} className="text-sm text-kyro-ink">Admin</Link>
                )}
                <button onClick={async () => { setOpenMenu(false); await logout(); }} className="text-sm text-kyro-mute text-left">Logout</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
